import React, { useEffect, useState } from "react";
import { Form, Input, Select, DatePicker, TimePicker } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import FormItem from "components/fields/FormItem";
import CrudButtons from "components/button/crudbuttons";
import SwitchField from "components/fields/SwitchField";
import TextArea from "antd/es/input/TextArea";

import {
	getLeaveDetails,
	doLeaveCreate,
	getScheduledOutTime,
	doLeaveUpdate,
	getAvailableLeaveDays,
	getWorkingDates,
} from "../../../../redux/leave/leaveState";

import {
	DATE_FORMAT,
	ROLE_SUPER_ADMIN,
	TIME_FORMAT,
} from "variables/constants";
import {
	MODULE_LEAVE,
} from "variables/modules";

import { requiredField } from "utils/crudSagaHelper";

const LeaveForm = ({
	onCancel,
	submitLabel,
	...props
}) => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();

	const { recordId, mode } = props;
	const { RangePicker } = DatePicker;
	const [rangeStartDate, setRangeStartDate] = useState(null);
	const isUnpaid = Form.useWatch("lve_is_unpaid", form);
	const selectedLeaveTypeId = Form.useWatch("lve_type_id", form);
	
	const recordData = useSelector((state) => state.leave.leaveDetails?.data);
	const scheduledOutTime = useSelector((state) => state.leave.scheduledOutTime?.data);
	const authUser = useSelector((state) => state.auth.user);
	const availableLeaveDays = useSelector((state) => state.leave.availableLeaveDays?.data);
	const workingDates = useSelector((state) => state.leave.workingDates?.data?.working_dates || []);
	const employeeList = recordData?.employeeList || [];
	const selectedEmployee = employeeList.find(
		(employee) =>
			employee.value ===
			(authUser?.user_level === ROLE_SUPER_ADMIN
				? form.getFieldValue("lve_user_id")
				: authUser?.id)
	);

	const toggleUnpaid =
		[29, 30].includes(Number(selectedLeaveTypeId)) &&
		selectedEmployee?.is_probationary === true &&
		Number(selectedEmployee?.leave_eligibility_period) === 1;

	const doCreate = (data) => dispatch(doLeaveCreate(data));
	const doUpdate = (data) => dispatch(doLeaveUpdate(data));

	const [computedDays, setComputedDays] = useState("");
	const [computedHours, setComputedHours] = useState("");
	const [showAvailableBalance, setShowAvailableBalance] = useState(false);

	useEffect(() => {
		dispatch(getLeaveDetails(recordId));
	}, [dispatch, recordId]);

	useEffect(() => {
		if (toggleUnpaid) {
			form.setFieldValue("lve_is_unpaid", true);
		}
	}, [toggleUnpaid, form]);

	useEffect(() => {
		if (!recordData?.lve_user_id || !recordData?.lve_type_id) {
			return;
		}

		dispatch(
			getAvailableLeaveDays({
				user_id: recordData.lve_user_id,
				leave_type_id: recordData.lve_type_id,
				exclude_leave_id: recordData.lve_id || null,
			})
		);
	}, [dispatch, recordData]);

	const fetchAvailableLeaveDays = () => {
		setShowAvailableBalance(false);

		const userId =
			authUser?.user_level === ROLE_SUPER_ADMIN
				? form.getFieldValue("lve_user_id")
				: authUser?.id;

		const leaveTypeId = form.getFieldValue("lve_type_id");

		if (!userId || !leaveTypeId) {
			return;
		}

		dispatch(
			getAvailableLeaveDays({
				user_id: userId,
				leave_type_id: leaveTypeId,
				exclude_leave_id: recordData?.lve_id || null,
			})
		);

		setShowAvailableBalance(true);
	};
	
	useEffect(() => {
		if (!recordData) {
			return;
		}

		const dateRange =
			recordData.lve_date_from && recordData.lve_date_to
				? [
					dayjs(recordData.lve_date_from),
					dayjs(recordData.lve_date_to),
				]
				: [];

		const undertimeFrom = recordData.lve_undertime_from
			? dayjs(recordData.lve_undertime_from, TIME_FORMAT)
			: null;

		const undertimeTo = recordData.lve_undertime_to
			? dayjs(recordData.lve_undertime_to, TIME_FORMAT)
			: null;

		form.resetFields();

		form.setFieldsValue({
			...recordData,

			lve_is_undertime: Boolean(recordData.lve_is_undertime),
			lve_is_unpaid: toggleUnpaid
				? true
				: Boolean(recordData.lve_is_unpaid),

			lve_date_range: dateRange,

			lve_undertime_from: undertimeFrom,
			lve_undertime_to: undertimeTo,
		});

		setComputedDays(
			dateRange.length === 2
				? dateRange[1].diff(dateRange[0], "day") + 1
				: ""
		);

		if (undertimeFrom && undertimeTo) {
			calculateUndertimeHours(
				undertimeFrom,
				undertimeTo
			);
		} else {
			setComputedHours("");
		}
	}, [recordData, form]);

	useEffect(() => {
		if (!scheduledOutTime?.scheduled_time_out) {
			return;
		}

		const scheduled = dayjs(
			scheduledOutTime.scheduled_time_out,
			TIME_FORMAT
		);

		form.setFieldsValue({
			lve_undertime_from: scheduled,
		});
	}, [scheduledOutTime, form]);

	const calculateUndertimeHours = (scheduledOut, actualOut) => {
		if (!scheduledOut || !actualOut) {
			setComputedHours(0);
			return;
		}

		const minutes = scheduledOut.diff(actualOut, "minute");

		setComputedHours(
			Math.max(
				0,
				Number((minutes / 60).toFixed(2))
			)
		);
	};


	const onSubmit = (values) => {
		try {
			const payload = {
				...values,
				lve_id: recordData?.lve_id,
				lve_date_from:
					values.lve_date_range?.[0]?.format(DATE_FORMAT),
				lve_date_to:
					values.lve_date_range?.[1]?.format(DATE_FORMAT),
				lve_is_undertime:
					Number(Boolean(values.lve_is_undertime)),
				lve_is_unpaid:
					Number(Boolean(values.lve_is_unpaid)),
			};

			delete payload.lve_date_range;

			if (!payload.lve_user_id) {
				payload.lve_user_id = authUser?.id;
			}
			if (payload.lve_is_undertime) {
				payload.lve_undertime_from =
					payload.lve_undertime_from?.format(TIME_FORMAT);
				payload.lve_undertime_to =
					payload.lve_undertime_to?.format(TIME_FORMAT);
			}
			recordData?.lve_id > 0
				? doUpdate(payload)
				: doCreate(payload);
		} catch (error) {
			console.error("Save failed:", error);
		}
	};

	return (
		<Form
			form={form}
			className={mode !== "view" ? "form" : ""}
			layout="vertical"
			onFinish={onSubmit}
		>
			<FormItem
				name="lve_id"
				hidden
			>
				<Input type="hidden" />
			</FormItem>

			<FormItem
				name="lve_status_id"
				hidden
			>
				<Input type="hidden" />
			</FormItem>

			<div
				className={`grid gap-4 ${
					authUser?.user_level === ROLE_SUPER_ADMIN
						? "grid-cols-1 lg:grid-cols-3"
						: "grid-cols-1 lg:grid-cols-2"
				}`}
			>
				{authUser?.user_level === ROLE_SUPER_ADMIN &&
					mode !== "view" && (
						<FormItem
							className="font-medium mb-2"
							label="Employee"
							required
						>
							<FormItem
								name="lve_user_id"
								noStyle
								rules={[requiredField]}
							>
								<Select
									showSearch
									allowClear
									className="input-field"
									placeholder="Select Employee"
									options={recordData?.employeeList}
									optionFilterProp="label"
									onChange={fetchAvailableLeaveDays}
								/>
							</FormItem>
						</FormItem>
					)}

				<FormItem
					className="font-medium mb-2"
					name="lve_type_id"
					label="Leave Type"
					rules={[requiredField]}
				>
					<Select
						allowClear
						showSearch
						className="input-field"
						options={recordData?.leaveTypeList}
						placeholder="Select leave type"
						onChange={fetchAvailableLeaveDays}
					/>
				</FormItem>

				<FormItem
					className="font-medium mb-2"
					name="lve_date_range"
					label="Date Range"
					rules={[requiredField]}
					formExtra={
						!isUnpaid &&
						showAvailableBalance &&
						availableLeaveDays?.available_leave_days != null && (
							<span className="text-gray-500">
								Available Balance: {
									Math.max(
										0,
										Number(
											availableLeaveDays.available_leave_days.toFixed(2)
										)
									)
								}
							</span>
						)
					}
				>
					<RangePicker
						className="input-field w-full"
						format={DATE_FORMAT}
						placeholder={["Start date", "End date"]}
						disabledDate={(current) => {
							if (isUnpaid || !rangeStartDate || 
								availableLeaveDays?.available_leave_days == null || !workingDates.length
							) {
								return false;
							}
							const maxDays = Number(
								availableLeaveDays.available_leave_days
							);
							const currentDate = current.format(DATE_FORMAT);
							const workingDatesUntilCurrent = workingDates.filter(
								(date) =>
									date >= rangeStartDate.format(DATE_FORMAT) &&
									date <= currentDate
							);
							return workingDatesUntilCurrent.length > maxDays;
						}}

						onCalendarChange={(dates) => {
							if (dates?.[0]) {
								setRangeStartDate(dates[0]);

								const userId =
									authUser?.user_level === ROLE_SUPER_ADMIN
										? form.getFieldValue("lve_user_id")
										: authUser?.id;

								if (userId) {
									dispatch(
										getWorkingDates({
											user_id: userId,
											date_from: dates[0].format(DATE_FORMAT),
											date_to: dates[0]
												.add(1, "year")
												.format(DATE_FORMAT),
										})
									);
								}
							} else {
								setRangeStartDate(null);
							}
						}}

						onChange={(dates) => {
							setComputedHours(0);
							if (!dates || dates.length !== 2) {
								setComputedDays(0);
								return;
							}

							const from = dates[0].format(DATE_FORMAT);
							const to = dates[1].format(DATE_FORMAT);
							const days = workingDates.filter(
								(date) => date >= from && date <= to
							).length;

							setComputedDays(days);
							const userId =
								authUser?.user_level === ROLE_SUPER_ADMIN
									? form.getFieldValue("lve_user_id")
									: authUser?.id;

							if (dates[0] && userId) {
								dispatch(
									getScheduledOutTime({
										userId,
										date: dates[0].format(DATE_FORMAT),
									})
								);
							}
						}}
					/>
				</FormItem>
			</div>

			<Form.Item
				noStyle
				shouldUpdate={(prevValues, currentValues) =>
					prevValues.lve_is_undertime !==
					currentValues.lve_is_undertime
				}
			>
				{({ getFieldValue }) => {
					const isUndertime =
						getFieldValue("lve_is_undertime");

					return (
						<>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
								<FormItem
									label={
										isUndertime
											? "Undertime Hours"
											: "Count"
									}
									className="font-medium mb-2"
								>
									<Input
										value={
											isUndertime
												? `${computedHours} hour(s)`
												: computedDays
										}
										readOnly
										className="input-field"
										placeholder={
											isUndertime
												? "0 hour(s)"
												: "0"
										}
									/>
								</FormItem>

								<div className="grid grid-cols-2 gap-4">
									<FormItem
										label="Is Undertime"
										name="lve_is_undertime"
										className="font-medium mb-2"
										valuePropName="checked"
									>
										<SwitchField
											id="lve_is_undertime"
											type="text"
											labelYes="Yes"
											labelNo="No"
										/>
									</FormItem>

									<FormItem
										label="Is Unpaid"
										name="lve_is_unpaid"
										className="font-medium mb-2"
										valuePropName="checked"
									>
										<SwitchField
											id="lve_is_unpaid"
											type="text"
											labelYes="Yes"
											labelNo="No"
											disabled={toggleUnpaid}
										/>
									</FormItem>
								</div>
							</div>

							{isUndertime && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<FormItem
										name="lve_undertime_from"
										label="Scheduled Time Out"
									>
										<TimePicker
											className="input-field w-full"
											format="HH:mm"
											disabled
										/>
									</FormItem>

									<FormItem
										name="lve_undertime_to"
										label="Actual Time Out"
										rules={[requiredField]}
									>
										<TimePicker
											className="input-field w-full"
											format="HH:mm"
											disabled={
												!scheduledOutTime?.scheduled_time_out
											}
											onChange={(time) => {
												calculateUndertimeHours(
													form.getFieldValue(
														"lve_undertime_from"
													),
													time
												);
											}}
										/>
									</FormItem>
								</div>
							)}

							<FormItem
								className="font-medium mb-2 md:col-span-3"
								name="lve_reason"
								label="Reason"
								rules={[requiredField]}
							>
								<TextArea
									id="lve_reason"
									className="input-field"
									maxLength={100}
									placeholder={
										isUndertime
											? "Please provide reason for undertime..."
											: "Please provide reason for leave..."
									}
								/>
							</FormItem>
						</>
					);
				}}
			</Form.Item>

			<div className="mt-4">
				{mode === "edit" && (
					<CrudButtons
						mode={mode}
						recordId={recordData?.lve_id ?? 0}
						isModal
						modalId={
							recordData?.lve_id > 0
								? "edit_leave"
								: "add_leave"
						}
						cancelLabel="Cancel"
						doHideModal={onCancel}
						submitLabel="Submit"
						moduleId={MODULE_LEAVE}
					/>
				)}
			</div>
		</Form>
	);
};

export default LeaveForm;